import * as React from 'react';
import {
  List,
  Edit,
  Create,
  SimpleForm,
  TextField,
  EditButton,
  TextInput,
  BooleanInput,
  CheckboxGroupInput,
  ImageInput,
  ImageField,
  required,
  ReferenceInput,
  SelectInput,
  ReferenceField,
  BooleanField,
  ReferenceArrayField,
  SingleFieldList,
  ChipField,
  useEditContext,
  useTranslate,
  useLocaleState,
} from 'react-admin';

// Components
import EmptyList from '../components/misc/EmptyList';
import InputRow from '../components/form/InputRow';
import SelectUserByEmailInput from '../components/form/SelectUserByEmailInput';
import ResizableDatagrid from '../components/table/ResizableDatagrid';

// Entity
const entity = 'salePoints';

const getPaymentChoices = (translate) => [
  { id: 'cc', name: translate('salePoints.method_cc', { _: 'Kredi Kartı' }) },
  { id: 'cash', name: translate('salePoints.method_cash', { _: 'Nakit' }) },
  { id: 'btc', name: translate('salePoints.method_btc', { _: 'Bitcoin' }) },
  { id: 'transfer', name: translate('salePoints.method_transfer', { _: 'Havale / EFT' }) },
];

export const SalePointList = (props) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const localeNameKey = locale === 'tr' ? 'name.tr' : 'name.en';
  const paymentChoices = getPaymentChoices(translate);

  const filters = [
    <TextInput
      source="name"
      label={translate('salePoints.name', { _: 'Satış Noktası Adı' })}
      alwaysOn
      key="name"
    />,
    <ReferenceInput
      source="city"
      reference="cities"
      label={translate('salePoints.city', { _: 'Şehir' })}
      alwaysOn
      key="city"
    >
      <SelectInput source={localeNameKey} optionText={localeNameKey} />
    </ReferenceInput>,
    <BooleanInput
      source="isActive"
      label={translate('salePoints.active_filter', { _: 'Aktiflik Filtresi' })}
      key="active"
    />,
    <CheckboxGroupInput source="paymentMethods" choices={paymentChoices} key="pm" />,
  ];

  return (
    <List
      {...props}
      empty={<EmptyList entity={entity} />}
      title={translate('salePoints.title', { _: 'Satış Noktaları' })}
      filters={filters}
      exporter={false}
    >
      <ResizableDatagrid storageKey="gk-datagrid-widths:salePoints" bulkActionButtons={false}>
        <TextField
          source="name"
          label={translate('resources.salePoints.fields.name', { _: 'Satış Noktası Adı' })}
        />
        <ReferenceField
          source="city"
          reference="cities"
          label={translate('resources.salePoints.fields.city', { _: 'Şehir' })}
          link={false}
        >
          <TextField source={localeNameKey} />
        </ReferenceField>
        <BooleanField
          source="isActive"
          label={translate('resources.salePoints.fields.isActive', { _: 'Aktif mi?' })}
        />
        <ReferenceArrayField
          source="managers"
          reference="users"
          label={translate('resources.salePoints.fields.managers', { _: 'Yöneticiler' })}
        >
          <SingleFieldList linkType={false}>
            <ChipField source="name" />
          </SingleFieldList>
        </ReferenceArrayField>
        <EditButton />
      </ResizableDatagrid>
    </List>
  );
};

export const SalePointCreate = (props) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const localeNameKey = locale === 'tr' ? 'name.tr' : 'name.en';
  const paymentChoices = getPaymentChoices(translate);

  return (
    <Create title={translate('salePoints.new_salepoint', { _: 'Yeni Satış Noktası' })} {...props}>
      <SimpleForm redirect={`/${entity}`}>
        <h3>{translate('promoters.details', { _: 'Detaylar' })}</h3>
        <TextInput
          source="name"
          label={translate('salePoints.name', { _: 'Satış Noktası Adı' })}
          fullWidth
          autoComplete="off"
          validate={required()}
        />
        <TextInput
          source="telephone"
          label={translate('salePoints.telephone', { _: 'Telefon' })}
          fullWidth
          autoComplete="off"
          validate={required()}
        />
        <TextInput
          source="description"
          label={translate('salePoints.description', { _: 'Açıklama' })}
          fullWidth
          autoComplete="off"
          rows={3}
          multiline
        />

        <h3>{translate('venues.address_info', { _: 'Adres Bilgileri' })}</h3>
        <ReferenceInput source="city" reference="cities">
          <SelectInput source="name" optionText={localeNameKey} fullWidth validate={required()} />
        </ReferenceInput>
        <TextInput
          source="address"
          label={translate('salePoints.address', { _: 'Adres' })}
          fullWidth
          autoComplete="off"
          validate={required()}
        />
        <InputRow>
          <TextInput
            source="coordinates.long"
            label={translate('salePoints.longitude', { _: 'Boylam' })}
            fullWidth
            autoComplete="off"
          />
          <TextInput
            source="coordinates.lat"
            label={translate('salePoints.latitude', { _: 'Enlem' })}
            fullWidth
            autoComplete="off"
          />
        </InputRow>

        <h3>{translate('salePoints.photo_logo', { _: 'Fotoğraf (Logo)' })}</h3>
        <ImageInput source="banner" label=" " accept="image/*" defaultValue={null}>
          <ImageField source="src" title="title" />
        </ImageInput>

        <h3>{translate('salePoints.payment_methods', { _: 'Ödeme Yöntemleri' })}</h3>
        <CheckboxGroupInput
          source="paymentMethods"
          choices={paymentChoices}
          validate={required()}
        />

        <h3>{translate('venues.managers', { _: 'Yöneticiler' })}</h3>
        <SelectUserByEmailInput source={'managers'} multiple={true} required={true} />

        <div style={{ visibility: 'hidden' }}>
          <BooleanInput
            source="isActive"
            value={true}
            checked={true}
            label={translate('salePoints.show_on_website', {
              _: 'Satış noktasını web sitesinde göster',
            })}
            helperText={translate('salePoints.save_notice', { _: 'Kayıt sonrası geçerli olur.' })}
            hidden={true}
            disabled={true}
          />
        </div>
      </SimpleForm>
    </Create>
  );
};

export const SalePointEdit = (props) => {
  const translate = useTranslate();
  return (
    <Edit
      title={translate('salePoints.edit_salepoint', { _: 'Satış Noktasını Düzenle' })}
      {...props}
    >
      <SimpleForm redirect={`/${entity}`}>
        <EditForm />
      </SimpleForm>
    </Edit>
  );
};

const EditForm = () => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const localeNameKey = locale === 'tr' ? 'name.tr' : 'name.en';
  const paymentChoices = getPaymentChoices(translate);
  const { record, isLoading } = useEditContext();
  if (isLoading) return null;

  return (
    <React.Fragment>
      <h3>{translate('pushNotifications.actions', { _: 'İşlemler' })}</h3>
      <BooleanInput
        source="isActive"
        label={translate('salePoints.show_on_website', {
          _: 'Satış noktasını web sitesinde göster',
        })}
        helperText={translate('salePoints.save_notice', {
          _: 'Değişikliklerin geçerli olması için kaydetmeniz gerekir.',
        })}
      />

      <h3>{translate('promoters.details', { _: 'Detaylar' })}</h3>
      <TextInput
        source="name"
        label={translate('salePoints.name', { _: 'Satış Noktası Adı' })}
        fullWidth
        autoComplete="off"
        validate={required()}
      />
      <ReferenceInput source="city" reference="cities">
        <SelectInput source="name" optionText={localeNameKey} fullWidth validate={required()} />
      </ReferenceInput>
      <InputRow>
        <TextInput
          source="coordinates.long"
          label={translate('salePoints.longitude', { _: 'Boylam' })}
          fullWidth
          autoComplete="off"
        />
        <TextInput
          source="coordinates.lat"
          label={translate('salePoints.latitude', { _: 'Enlem' })}
          fullWidth
          autoComplete="off"
        />
      </InputRow>
      <TextInput
        source="description"
        label={translate('salePoints.description', { _: 'Açıklama' })}
        fullWidth
        autoComplete="off"
        rows={3}
        multiline
      />

      <h3>{translate('salePoints.photo_logo', { _: 'Fotoğraf (Logo)' })}</h3>
      <ImageInput source="banner" label=" " accept="image/*" defaultValue={null}>
        <ImageField source="src" title="title" />
      </ImageInput>

      <h3>{translate('salePoints.payment_methods', { _: 'Ödeme Yöntemleri' })}</h3>
      <CheckboxGroupInput source="paymentMethods" choices={paymentChoices} validate={required()} />

      <h3>{translate('venues.managers', { _: 'Yöneticiler' })}</h3>
      <SelectUserByEmailInput source={'managers'} multiple={true} record={record} required={true} />
    </React.Fragment>
  );
};
